import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Grid, FormControl, FormHelperText, InputLabel, Select, MenuItem, Avatar, ListItemAvatar, ListItemText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { positionOptions } from '../../../utils/Enums';
import { permissionOptions } from '../../../utils/Enums';
import './Locations.scss';

const EditLocationModal = ({ open, onClose, onSave, location, users, businessUnits = [] }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    manager_id: '',
    business_unit_id: '',
    is_plant: false,
    latitude: '',
    longitude: '',
  });

  const [errors, setErrors] = useState({});
  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionId) => userPermissions.includes(permissionId);
  const canManageLocationStructure = hasPermission(
    permissionOptions.find((p) => p.label === 'VIEW_BUSINESS_UNITS').id
  );

  useEffect(() => {
    if (location) {
      const managerRoles = [positionOptions.find(option => option.label === 'ceo')?.id, positionOptions.find(option => option.label === 'manager')?.id];
      const filteredUsers = users?.filter(user => managerRoles.includes(user.position)) || [];
      const currentManagerEmail = location.manager?.email ?? '';
      const validManagerId = filteredUsers.some(user => user.email === currentManagerEmail) ? currentManagerEmail : '';

      setFormData({
        name: location.name || '',
        description: location.description || '',
        address: location.address || '',
        manager_id: validManagerId || '',
        business_unit_id: location.businessUnitId ?? '',
        is_plant: location.isPlant ?? false,
        latitude: location.latitude != null ? String(location.latitude) : '',
        longitude: location.longitude != null ? String(location.longitude) : '',
      });
    }
    if (open) {
      setErrors({});
    }
  }, [location, open, users]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleIsPlantChange = (e) => {
    const value = e.target.value === 'true'; // Convertir a booleano
    setFormData({ ...formData, is_plant: value });
  };

  const validate = () => {
    let tempErrors = {};
    let isValid = true;

    if (!formData.name) {
      tempErrors.name = t('name_required');
      isValid = false;
    }
    if (!formData.description) {
      tempErrors.description = t('description_required');
      isValid = false;
    }
    if (!formData.address) {
      tempErrors.address = t('address_required');
      isValid = false;
    }
    setErrors(tempErrors);
    return isValid;
  };

  const handleSave = () => {
    if (validate()) {
      const toSave = {
        ...formData,
        manager_id: formData.manager_id === '' ? null : formData.manager_id,
        business_unit_id: formData.business_unit_id === '' ? null : Number(formData.business_unit_id),
        latitude: formData.latitude === '' ? null : Number(formData.latitude),
        longitude: formData.longitude === '' ? null : Number(formData.longitude),
      };
      onSave(toSave);
      onClose();
    }
  };

  const managerRoles = [positionOptions.find(option => option.label === 'ceo')?.id, positionOptions.find(option => option.label === 'manager')?.id];
  const filteredUsers = users?.filter(user => managerRoles.includes(user.position)) || [];
  const getBusinessUnitLabel = (businessUnitId) => {
    if (businessUnitId === '' || businessUnitId === null || businessUnitId === undefined) {
      return t('location_without_franchise');
    }
    return businessUnits.find((unit) => String(unit.id) === String(businessUnitId))?.name || t('location_without_franchise');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle><span className="roboto-regular">{t('edit_location')}</span></DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('name')}
              type="text"
              fullWidth
              name="name"
              style={{ marginTop: '8px' }}
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('description')}
              type="text"
              fullWidth
              name="description"
              value={formData.description}
              onChange={handleChange}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('address')}
              type="text"
              fullWidth
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('latitude') || 'Latitud'}
              type="number"
              fullWidth
              name="latitude"
              value={formData.latitude}
              onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
              inputProps={{ step: 'any', min: -90, max: 90 }}
              placeholder="-34.9011"
              helperText={t('location_origin_courier') || 'Origen para envíos (cadetería)'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label={t('longitude') || 'Longitud'}
              type="number"
              fullWidth
              name="longitude"
              value={formData.longitude}
              onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
              inputProps={{ step: 'any', min: -180, max: 180 }}
              placeholder="-56.1645"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel shrink>{t('location_franchise')}</InputLabel>
              <Select
                name="business_unit_id"
                value={formData.business_unit_id}
                onChange={handleChange}
                label={t('location_franchise')}
                notched
                displayEmpty
                renderValue={getBusinessUnitLabel}
                disabled={!canManageLocationStructure}
              >
                <MenuItem value="">{t('location_without_franchise')}</MenuItem>
                {businessUnits.map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{t('location_franchise_helper')}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t('type_location')}</InputLabel>
              <Select
                name="is_plant"
                value={formData.is_plant?.toString()} // Aseguramos que sea string
                onChange={handleIsPlantChange}
                label={t('location_type')}
                disabled={!canManageLocationStructure}
              >
                <MenuItem value="false">{t('branch')}</MenuItem>
                <MenuItem value="true">{t('plant')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.manager_id}>
              <InputLabel>{`${t('manager')} (${t('optional')})`}</InputLabel>
              <Select
                name="manager_id"
                value={formData.manager_id}
                onChange={handleChange}
                label={`${t('manager')} (${t('optional')})`}
              >
                <MenuItem value="">
                  <em>{t('optional')}</em>
                </MenuItem>
                {filteredUsers.map(user => (
                  <MenuItem key={user.email} value={user.email}>
                    <ListItemAvatar>
                      <Avatar src={user.profilePictureUrl} alt={`${user.firstName} ${user.lastName}`} />
                    </ListItemAvatar>
                    <ListItemText primary={`${user.firstName} ${user.lastName}`} />
                  </MenuItem>
                ))}
              </Select>
              {errors.manager_id && <p style={{ color: 'red' }}>{errors.manager_id}</p>}
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button onClick={handleSave} variant="contained" color="primary">{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLocationModal;
